import { createContext, use } from 'react';
import type { User } from 'firebase/auth';
import type {
  AppSettings,
  Copy,
  DiscFormat,
  Locale,
  LocalePreference,
  Movie,
  ParsedImport,
  TmdbClient,
  Translator,
  Watch,
  WishlistItem,
} from '@physical-movies/core';

/**
 * Every React context in one place.
 *
 * Contexts and their hooks live apart from the components that provide them so
 * each provider module exports a component and nothing else, which keeps fast
 * refresh working.
 */

function required<T>(value: T | null, name: string): T {
  if (value === null) throw new Error(`${name} was used outside its provider.`);
  return value;
}

// --- Theme -------------------------------------------------------------------

export type ThemePreference = 'system' | 'light' | 'dark';

export interface ThemeContextValue {
  preference: ThemePreference;
  /** What the preference actually resolves to right now. */
  resolved: 'light' | 'dark';
  setPreference(preference: ThemePreference): void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);
export const useTheme = () => required(use(ThemeContext), 'useTheme');

// --- Language ----------------------------------------------------------------

export interface I18nContextValue {
  t: Translator;
  locale: Locale;
  preference: LocalePreference;
  setPreference(preference: LocalePreference): void;
}

export const I18nContext = createContext<I18nContextValue | null>(null);
export const useI18n = () => required(use(I18nContext), 'useI18n');

// --- Toasts ------------------------------------------------------------------

export type ToastTone = 'default' | 'danger';

export interface ToastContextValue {
  show(message: string, tone?: ToastTone): void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);
export const useToast = () => required(use(ToastContext), 'useToast');

// --- Auth --------------------------------------------------------------------

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in';

/**
 * `kind` is translated by the sign-in screen, so the provider stays
 * language-agnostic. `code` is the raw Firebase code, carried through
 * untranslated: on a self-hosted app the person seeing the error is usually
 * the person who can fix it, and "something went wrong" tells them nothing.
 */
export interface AuthError {
  kind: 'failed' | 'popup-blocked';
  code: string | null;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  busy: boolean;
  error: AuthError | null;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
export const useAuth = () => required(use(AuthContext), 'useAuth');

// --- Collection data ---------------------------------------------------------

/**
 * `denied` is the normal state for an account that is not on the allowlist —
 * the app is locked, not broken.
 */
export type CollectionStatus = 'loading' | 'ready' | 'denied' | 'error';

export interface NewMovieInput {
  tmdbId: number | null;
  title: string;
  titleDe?: string | null;
  originalTitle?: string | null;
  year: number | null;
  releaseDate?: string | null;
  runtime: number | null;
  posterPath?: string | null;
  backdropPath?: string | null;
  overviewEn?: string | null;
  overviewDe?: string | null;
  genreIds?: number[];
  directors?: string[];
  copies: Copy[];
}

export interface WatchInput {
  watchedAt: string;
  rating?: number | null;
  note?: string | null;
}

/**
 * What a caller must supply to put something on the wishlist. The trash and
 * buyable bookkeeping have sensible defaults, so only the film itself is
 * required.
 */
export type NewWishlistInput = Omit<
  WishlistItem,
  'id' | 'addedAt' | 'genreIds' | 'buyable' | 'deletedAt'
> &
  Partial<Pick<WishlistItem, 'genreIds' | 'buyable'>>;

export interface ImportSummary {
  movies: number;
  watches: number;
  wishlist: number;
}

export interface CollectionContextValue {
  status: CollectionStatus;
  movies: Movie[];
  watches: Watch[];
  /** Live entries only — anything in the trash is kept out of here. */
  wishlist: WishlistItem[];
  /** Deleted entries still inside their retention window. */
  wishlistTrash: WishlistItem[];
  /** Where the discs are, and the sort you prefer. Never null: defaults stand in. */
  settings: AppSettings;
  /** Indexed for detail lookups. */
  movieById: Map<string, Movie>;
  watchesByMovie: Map<string, Watch[]>;

  addMovie(input: NewMovieInput): Promise<string>;
  updateMovie(id: string, patch: Partial<Movie>): Promise<void>;
  deleteMovie(movie: Movie): Promise<void>;
  setCopies(movie: Movie, copies: Copy[]): Promise<void>;

  logWatch(movie: Movie, input: WatchInput): Promise<void>;
  updateWatch(watch: Watch, patch: Partial<Watch>): Promise<void>;
  deleteWatch(watch: Watch): Promise<void>;

  addWishlistItem(item: NewWishlistInput): Promise<void>;
  updateWishlistItem(id: string, patch: Partial<WishlistItem>): Promise<void>;
  /** Soft delete: the entry moves to the trash. */
  trashWishlistItem(id: string): Promise<void>;
  restoreWishlistItem(id: string): Promise<void>;
  /** Irreversible. Only offered from the trash. */
  deleteWishlistItem(id: string): Promise<void>;
  moveWishlistItemToCollection(item: WishlistItem, format: DiscFormat): Promise<string>;

  saveSettings(settings: AppSettings): Promise<void>;

  importCollection(parsed: ParsedImport): Promise<ImportSummary>;
}

export const CollectionContext = createContext<CollectionContextValue | null>(null);
export const useCollection = () => required(use(CollectionContext), 'useCollection');

// --- TMDB --------------------------------------------------------------------

export const TmdbContext = createContext<TmdbClient | null>(null);
export const useTmdb = () => required(use(TmdbContext), 'useTmdb');
